// -----------------------------------------------------------------------------
// PostSendExpandedCard
//
// Shown as the right-hand panel (70%) of the post-send swap layout after an
// email / treatment-plan / review-request is sent successfully.
//
// Presents the newly-featured NEXT task with expanded detail (title, type icon,
// description, due date) and an immediately-accessible "Change due date" button
// so the clinician can schedule the follow-up task without leaving the popup.
//
// The due-date dialog matches the UpNextCardV2 dialog (same presets, same
// StaticDatePicker). Preset clicks auto-confirm (single click = saved). Calendar
// picks require a "Save" button.
//
// Due-date update wires to onChangeDueDate(item, Date) which maps to
// handleChangeDueDate in AttendedGuidedChecklistV2 → PATCH
// /api/v1/practices/{p}/tasks/{id}/ { due_at } — gated on confirmed 200.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import moment from 'moment';
import {
    Box, Typography, Button, Stack,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import Iconify from 'src/components/Iconify';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { ACTION_META, DEFAULT_ACTION_META } from '../actionMeta';
import TaskAssigneeRow from '../TaskAssigneeRow';

export default function PostSendExpandedCard({ item, onChangeDueDate, practiceId }) {
    const [calOpen, setCalOpen] = useState(false);
    const [calValue, setCalValue] = useState(null);

    const handleOpenCal = () => {
        setCalValue(item?.due_at ? moment(item.due_at) : null);
        setCalOpen(true);
    };
    const handleCalCancel = () => setCalOpen(false);
    // Single-click confirm for presets.
    const handlePresetSelect = (date) => {
        if (onChangeDueDate && item) onChangeDueDate(item, date.toDate());
        setCalOpen(false);
    };
    // Calendar date picked → needs explicit "Save".
    const handleCalConfirm = () => {
        if (calValue && onChangeDueDate && item) onChangeDueDate(item, calValue.toDate());
        setCalOpen(false);
    };

    if (!item) return null;

    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const title = item.title || item.name || meta.label;

    return (
        <Box
            data-testid="post-send-expanded-card"
            sx={{
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                p: 2,
                border: '1px solid rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '100%',
            }}
        >
            {/* Eyebrow */}
            <Typography
                data-testid="post-send-eyebrow"
                component="span"
                sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6e6e73',
                }}
            >
                Next up
            </Typography>

            {/* Icon + title + description */}
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                    data-testid="post-send-icon"
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: `${meta.color}1A`,
                        border: `1.5px solid ${meta.color}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon={meta.icon} width={22} height={22} sx={{ color: meta.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        data-testid="post-send-title"
                        sx={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#1d1d1f',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                        }}
                    >
                        {title}
                    </Typography>
                    {item.description && (
                        <Typography
                            data-testid="post-send-description"
                            sx={{
                                fontSize: '12px',
                                color: '#3c3c43',
                                letterSpacing: '-0.005em',
                                mt: 0.5,
                                lineHeight: 1.4,
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {/* Due date chip + "Change / Set due date" button */}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" rowGap={0.5}>
                {item.due_at && (
                    <Typography
                        data-testid="post-send-due-timestamp"
                        sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#86868b',
                            letterSpacing: '-0.005em',
                        }}
                    >
                        {moment(item.due_at).format('ddd D MMM, HH:mm')}
                    </Typography>
                )}
                {onChangeDueDate && (
                    <Button
                        data-testid="post-send-change-due-date-btn"
                        size="small"
                        aria-label={item.due_at ? 'Change due date for next task' : 'Set due date for next task'}
                        startIcon={<Iconify icon="mdi:calendar-edit" width={13} height={13} />}
                        onClick={handleOpenCal}
                        sx={{
                            color: '#078DEE',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            letterSpacing: '-0.005em',
                            bgcolor: 'rgba(7,141,238,0.08)',
                            borderRadius: '6px',
                            py: '3px',
                            px: '10px',
                            minWidth: 0,
                            '&:hover': { bgcolor: 'rgba(7,141,238,0.14)' },
                        }}
                    >
                        {item.due_at ? 'Change due date' : 'Set due date'}
                    </Button>
                )}
            </Stack>

            {/* Assignee row — editable */}
            {item.taskId && practiceId && (
                <Box data-testid="post-send-assignee-row">
                    <TaskAssigneeRow
                        practiceId={practiceId}
                        taskId={item.taskId}
                        initialAssignees={item.rawTask?.assignees || []}
                        size={20}
                    />
                </Box>
            )}

            {/* Due-date dialog — presets auto-confirm, calendar pick requires Save */}
            <Dialog
                data-testid="post-send-due-date-dialog"
                open={calOpen}
                onClose={handleCalCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                        Schedule next task
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                        {title}
                    </Typography>
                    {item.due_at && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
                            <Iconify icon="mdi:calendar-clock" width={14} height={14} sx={{ color: '#6e6e73' }} />
                            <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                Currently due {moment(item.due_at).format('ddd D MMM, HH:mm')}
                            </Typography>
                        </Stack>
                    )}
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {/* Quick presets — single-click confirm */}
                    <Stack direction="row" spacing={0.75} sx={{ px: 3, pt: 2, pb: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
                        {[
                            { label: 'Today',     getDate: () => moment().endOf('day') },
                            { label: 'Tomorrow',  getDate: () => moment().add(1, 'day').endOf('day') },
                            { label: 'In 3 days', getDate: () => moment().add(3, 'days').endOf('day') },
                            { label: 'Next week', getDate: () => moment().add(1, 'week').endOf('day') },
                            { label: 'In 2 weeks', getDate: () => moment().add(2, 'weeks').endOf('day') },
                        ].map((preset) => (
                            <Button
                                key={preset.label}
                                size="small"
                                onClick={() => handlePresetSelect(preset.getDate())}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    py: 0.4,
                                    px: 1.25,
                                    minWidth: 0,
                                    color: '#3c3c43',
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { bgcolor: 'rgba(7,141,238,0.10)', color: '#078DEE' },
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Stack>
                    <Box sx={{ px: 1, pb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <StaticDateTimePicker
                                displayStaticWrapperAs="desktop"
                                ampm={false}
                                value={calValue}
                                onChange={(newVal) => setCalValue(newVal)}
                                slotProps={{
                                    actionBar: { actions: [] },
                                    toolbar: { hidden: true },
                                }}
                                sx={{
                                    bgcolor: 'transparent',
                                    '& .MuiPickersDay-root.Mui-selected': { bgcolor: '#078DEE', '&:hover': { bgcolor: '#0a7bd1' } },
                                    '& .MuiPickersDay-today': { borderColor: '#078DEE' },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                    {calValue && (
                        <Box sx={{ px: 3, pb: 1.5 }}>
                            <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                New due date:{' '}
                                <Box component="span" sx={{ color: '#078DEE', fontWeight: 600 }}>
                                    {calValue.format('dddd D MMMM YYYY, HH:mm')}
                                </Box>
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fafafa' }}>
                    <Button
                        data-testid="post-send-due-date-cancel-btn"
                        onClick={handleCalCancel}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="post-send-due-date-confirm-btn"
                        variant="contained"
                        disableElevation
                        disabled={!calValue}
                        onClick={handleCalConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

PostSendExpandedCard.propTypes = {
    /** The newly-featured next task (from featuredOverride / effectiveUpNext). */
    item: PropTypes.object,
    /** Called with (item, newDate: Date) — maps to handleChangeDueDate in orchestrator. */
    onChangeDueDate: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PostSendExpandedCard.defaultProps = {
    item: null,
    onChangeDueDate: null,
    practiceId: null,
};
